/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import * as sdk from 'commerce-sdk-isomorphic'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import OcapiCustomObjects from './ocapi-custom-objects'
import StorefrontAPI from './storefront'
import ShopperBaskets from './shopper-baskets'
import OcapiShopperOrders from './ocapi-shopper-orders'
import OcapiShopperContent from './ocapi-shopper-content'
import OcapiShopperFolders from './ocapi-shopper-folders'
import OcapiShopperCustomers from './ocapi-shopper-customers'
import CustomShopperSearch from './custom-shopper-search'
import CustomShopperProducts from './custom-shopper-products'
import {isError} from './utils'
import EinsteinAPI from './einstein'
import OcapiShopperSite from './ocapi-shopper-site'
import OcapiShopperOrder from './ocapi-shopper-order'
import OcapiShopperContentSearch from './ocapi-shopper-content-search'
import OcapiShopperStores from './ocapi-shopper-stores'

/**
 * The configuration details for the connecting to the API.
 * @typedef {Object} ClientConfig
 * @property {string} [proxy] - URL to proxy fetch calls through.
 * @property {string} [headers] - Request headers to be added to requests.
 * @property {Object} [parameters] - API connection parameters for SDK.
 * @property {string} [parameters.clientId]
 * @property {string} [parameters.organizationId]
 * @property {string} [parameters.shortCode]
 * @property {string} [parameters.siteId]
 * @property {string} [parameters.version]
 */

/**
 * An object containing the customer's login credentials.
 * @typedef {Object} CustomerCredentials
 * @property {string} credentials.email
 * @property {string} credentials.password
 */

/**
 * Salesforce Customer object.
 * {@link https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customer}}
 * @typedef {Object} Customer
 */

/**
 * A wrapper class that proxies calls to the underlying commerce-sdk-isomorphic.
 * The sdk class instances are created automatically with the given config.
 */
class CommerceAPI {
    /**
     * Create an instance of the API with the given config.
     * @param {ClientConfig} config - The config used to instantiate SDK apis.
     */
    constructor(config = {}, auth) {
        const {proxyPath, ...restConfig} = config

        const appOrigin = getAppOrigin()
        // Client-side requests should be proxied via the configured path.
        const proxy = `${appOrigin}${proxyPath}`

        this._config = {proxy, ...restConfig}

        this.auth = auth

        if (this._config.einsteinConfig?.einsteinId) {
            this.einstein = new EinsteinAPI(this)
        }

        // TODO v3: disabled as the v3 preview is moved to @salesforce/commerce-sdk-react and requires react-query
        // this.isStorefrontPreview = detectStorefrontPreview()

        // A mapping of property names to the SDK class constructors we'll be
        // providing instances for.
        //
        // NOTE: `sendLocale` and `sendCurrency` for sending locale and currency info to the API:
        // - boolean, if you want to affect _all_ methods for a given API
        // - OR an array (listing the API's methods), if you want to affect only certain methods of an API
        const apiConfigs = {
            sapi: {
                api: StorefrontAPI,
                sendLocale: true
            },
            customObjects: {
                api: OcapiCustomObjects,
                sendCurrency: false
            },
            shopperCustomers: {
                api: sdk.ShopperCustomers,
                sendLocale: false
            },
            shopperCustomersOcapi: {
                api: OcapiShopperCustomers,
                sendLocale: false
            },
            shopperBaskets: {
                api: ShopperBaskets,
                sendLocale: true,
                sendCurrency: ['createBasket']
            },
            shopperExperience: {
                api: sdk.ShopperExperience
            },
            shopperGiftCertificates: {
                api: sdk.ShopperGiftCertificates
            },
            shopperLogin: {api: sdk.ShopperLogin, sendLocale: false},
            shopperOrders: {api: OcapiShopperOrders},
            shopperProducts: {
                api: CustomShopperProducts,
                sendCurrency: ['getProduct', 'getProducts']
            },
            shopperPromotions: {
                api: sdk.ShopperPromotions
            },
            shopperSeo: {
                api: sdk.ShopperSeo
            },
            shopperSearch: {
                api: CustomShopperSearch,
                sendCurrency: ['productSearch', 'getSearchSuggestions']
            },
            shopperContent: {api: OcapiShopperContent},
            shopperFolders: {api: OcapiShopperFolders},
            shopperOrder: {api: OcapiShopperOrder},
            shopperSite: {api: OcapiShopperSite},
            contentSearch: {api: OcapiShopperContentSearch},
            shopperStore: {api: OcapiShopperStores}
        }

        // Instantiate the SDK class proxies and create getters from our api mapping.
        // The proxy handlers are called when accessing any of the mapped SDK class
        // proxies, executing various pre-defined hooks for tapping into or modifying
        // the outgoing method parameters and/or incoming SDK responses
        Object.keys(apiConfigs).forEach((key) => {
            const SdkClass = apiConfigs[key].api
            this._sdkInstances = {
                ...this._sdkInstances,
                [key]: new Proxy(new SdkClass(this._config), {
                    get: (obj, prop) => {
                        if (typeof obj[prop] === 'function') {
                            return (...args) => {
                                const fetchOptions = args[0] || {}
                                const {locale, currency} = this._config

                                if (fetchOptions.ignoreHooks) {
                                    return obj[prop](...args)
                                }

                                // Inject the locale and currency to the API call via its parameters.
                                //
                                // NOTE: The commerce sdk isomorphic will complain if you pass parameters to
                                // it that it doesn't expect, this is why we only add the locale and currency
                                // to some of the API calls.

                                // By default we send the locale param and don't send the currency param.
                                const {sendLocale = true, sendCurrency = false} = apiConfigs[key]

                                const includeGlobalLocale = Array.isArray(sendLocale)
                                    ? sendLocale.includes(prop)
                                    : !!sendLocale

                                const includeGlobalCurrency = Array.isArray(sendCurrency)
                                    ? sendCurrency.includes(prop)
                                    : !!sendCurrency

                                fetchOptions.parameters = {
                                    ...(includeGlobalLocale ? {locale} : {}),
                                    ...(includeGlobalCurrency ? {currency} : {}),
                                    // Allowing individual API calls to override the global locale/currency
                                    ...fetchOptions.parameters
                                }

                                return this.willSendRequest(prop, ...args).then((newArgs) => {
                                    return obj[prop](...newArgs).then((res) =>
                                        this.didReceiveResponse(res, newArgs)
                                    )
                                })
                            }
                        }
                        return obj[prop]
                    }
                })
            }
            Object.defineProperty(this, key, {
                get: () => this._sdkInstances[key]
            })
        })
        this.getConfig = this.getConfig.bind(this)
    }

    /**
     * Returns the api client configuration
     * @returns {ClientConfig}
     */
    getConfig() {
        return this._config
    }

    /**
     * Executed before every proxied method call to the SDK. Provides the method
     * name and arguments. This can be overidden in a subclass to perform any
     * logging or modifications to arguments before the request is sent.
     * @param {string} methodName - The name of the sdk method that will be called.
     * @param {...*} args - Original arguments for the SDK method.
     * @returns {Promise<Array>} - Updated arguments that will be passed to the SDK method
     */
    async willSendRequest(methodName, ...params) {
        // We never need to modify auth request headers for these methods
        if (
            methodName === 'authenticateCustomer' ||
            methodName === 'authorizeCustomer' ||
            methodName === 'getAccessToken'
        ) {
            return params
        }

        const {access_token} = await this.auth.ready()

        // Apply the appropriate auth headers and return new options
        const [fetchOptions, ...restParams] = params
        const newFetchOptions = {
            ...fetchOptions,
            headers: {
                Authorization: `Bearer ${access_token}`,
                ...fetchOptions.headers
            }
            // In Storefront Preview mode, add cache breaker for all SCAPI's requests.
            // Otherwise, it's possible to get stale responses after the Shopper Context is set.
            // (i.e. in this case, we optimize for accurate data, rather than performance/caching)
            // TODO v3: disabled as the v3 preview is moved to @salesforce/commerce-sdk-react and requires react-query
            // parameters: {
            //     ...fetchOptions.parameters,
            //     ...(this.isStorefrontPreview ? {c_cache_breaker: Date.now()} : {})
            // }
        }
        return [newFetchOptions, ...restParams]
    }

    /**
     * Executed when receiving a response from an SDK request. The response data
     * can be mutated or inspected before being passed back to the caller. Should
     * be overidden in a subclass.
     * @param {*} response - The response from the SDK method call.
     * @param {Array} args - Original arguments for the SDK method.
     * @returns {*} - The response to be passed back to original caller.
     */
    didReceiveResponse(response, _args) {
        if (isError(response)) {
            return {...response, isError: true, message: response.detail}
        }

        return response
    }
}

export default CommerceAPI
